/**
 * @description Demand Days Picker Component
 * Displays peak/festival days information in the date picker
 */

import React, { useEffect, useState } from 'react';
import { Typography } from 'antd';
import moment from 'moment';
import { fetchDemandDays, formatDemandDaysForCalendar } from './demand-days-picker.module';
import './demand-days-picker.style.scss';

const { Text } = Typography;

/**
 * DemandDaysPicker Component
 * @param {Object} props - Component props
 * @param {moment} props.currentMonth - Current month being displayed in the date picker
 * @param {Function} props.onDataLoaded - Callback when data is loaded
 * @param {string|Array} props.origin - Origin city or array of origins (optional, defaults to 'all')
 * @param {string|Array} props.destination - Destination city or array of destinations (optional, defaults to 'all')
 */
export const DemandDaysPicker = ({ currentMonth, onDataLoaded, origin, destination }) => {
    const [demandDaysData, setDemandDaysData] = useState({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (currentMonth) {
            loadDemandDays(currentMonth);
        }
    }, [currentMonth, origin, destination]);

    const loadDemandDays = async (month) => {
        setLoading(true);
        
        // Get start and end dates for the current month
        const startDate = moment(month).startOf('month').format('YYYY-MM-DD');
        const endDate = moment(month).endOf('month').format('YYYY-MM-DD');

        try {
            const result = await fetchDemandDays(startDate, endDate, origin, destination);
            
            if (result.success) {
                const formattedData = formatDemandDaysForCalendar(result.data);
                setDemandDaysData(formattedData);
                
                if (onDataLoaded) {
                    onDataLoaded(formattedData);
                }
            }
        } catch (error) {
            console.error('Error loading demand days:', error);
        } finally {
            setLoading(false);
        }
    };

    return null; // This component doesn't render anything directly
};

/**
 * Get demand days info for a specific date
 * @param {string} date - Date in YYYY-MM-DD format
 * @param {Object} demandDaysData - Formatted demand days data
 * @returns {Array} - Array of demand day info for the date
 */
export const getDemandDaysForDate = (date, demandDaysData) => {
    if (!date || !demandDaysData) {
        return [];
    }
    
    const dateStr = moment(date).format('YYYY-MM-DD');
    return demandDaysData[dateStr] || [];
};

/**
 * Custom date cell render function for Ant Design DatePicker
 * @param {moment} current - Current date being rendered
 * @param {Object} demandDaysData - Formatted demand days data
 * @returns {JSX.Element} - Custom cell content
 */
export const dateFullCellRender = (current, demandDaysData) => {
    if (!current) {
        return <div>{current}</div>;
    }

    const dateStr = moment(current).format('YYYY-MM-DD');
    const demandDays = demandDaysData[dateStr] || [];
    const hasDemandDay = demandDays.length > 0;

    let isToday = dateStr == moment().format('YYYY-MM-DD');
    let className = isToday ? 'today-date-cell' : '';

    return (
        <div className={`custom-date-cell ${className}`}>
            <div className={`date-number ${hasDemandDay ? 'has-demand-day' : ''}`}>
                {current.date()}
            </div>
            {hasDemandDay && (
                <div className="demand-day-indicator">
                    <div className="demand-day-dot"></div>
                </div>
            )}
        </div>
    );
};

/**
 * Render demand days list for a specific date
 * @param {Array} demandDays - Array of demand day info
 * @returns {JSX.Element} - Demand days list
 */
export const renderDemandDaysList = (demandDays) => {
    if (!demandDays || demandDays.length === 0) {
        return null;
    }

    return (
        <div className="demand-days-list">
            <Text strong className="demand-days-title">Peak / Festival Days:</Text>
            {demandDays.map((day, index) => (
                <div key={index} className="demand-day-item">
                    <Text className="demand-day-description">{day.description}</Text>
                </div>
            ))}
        </div>
    );
};

/**
 * Render all demand days for the month
 * @param {Object} demandDaysData - All demand days data
 * @param {moment} currentMonth - Current month being displayed
 * @returns {JSX.Element} - Month demand days list
 */
export const renderMonthDemandDaysList = (demandDaysData, currentMonth) => {
    if (!demandDaysData || Object.keys(demandDaysData).length === 0) {
        return (
            <div className="month-demand-days-panel">
                <Text strong className="panel-title">Peak / Festival Days</Text>
                <div className="no-demand-days">
                    <Text type="secondary">No demand days</Text>
                </div>
            </div>
        );
    }

    // Filter dates for current month and sort them
    const monthDates = Object.keys(demandDaysData)
        .filter(date => moment(date).isSame(currentMonth, 'month'))
        .sort();

    if (monthDates.length === 0) {
        return (
            <div className="month-demand-days-panel">
                <Text strong className="panel-title">Peak / Festival Days</Text>
                <div className="no-demand-days">
                    <Text type="secondary">No demand days</Text>
                </div>
            </div>
        );
    }

    return (
        <div className="month-demand-days-panel">
            <Text strong className="panel-title">Peak / Festival Days ({monthDates.length})</Text>
            <div className="month-demand-days-list">
                {monthDates.map((date, index) => {
                    const demandDays = demandDaysData[date];
                    const formattedDate = moment(date).format('DD MMM, ddd');
                    
                    return (
                        <div key={index} className="month-demand-day-item">
                            <div className="demand-day-date">
                                <span className="date-indicator">•</span>
                                <Text strong>{formattedDate}</Text>
                            </div>
                            {demandDays.map((day, idx) => (
                                <div key={idx} className="demand-day-detail">
                                    <Text className="demand-day-desc">{day.description}</Text>
                                </div>
                            ))}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

/**
 * Custom render function for DatePicker calendar header
 * Shows demand days summary for the month
 * @param {Object} demandDaysData - Formatted demand days data
 * @param {moment} currentMonth - Current month being displayed
 * @returns {Function} - Header render function
 */
export const renderCalendarHeader = (demandDaysData, currentMonth) => {
    return ({ value, type, onChange, onTypeChange }) => {
        const monthDemandDays = Object.keys(demandDaysData).filter(date => {
            return moment(date).isSame(currentMonth, 'month');
        });

        return (
            <div className="calendar-header-with-demand-days">
                {monthDemandDays.length > 0 && (
                    <div className="month-demand-days-summary">
                        <Text type="secondary" className="summary-text">
                            {monthDemandDays.length} Peak/Festival day(s) this month
                        </Text>
                    </div>
                )}
            </div>
        );
    };
};

export default DemandDaysPicker;

